import { cloneElement, ReactElement, useMemo } from 'react';

import { useBreakpointValue } from '@bonusly/design-system';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { EmojiButton, MentionButton } from '..';
import { useActionInput } from '../../../context';
import { ActionModule } from '../../../types';
import { AttachmentButton } from '../attachment-button';
import { GifButton } from '../gif-button';
import { ImageButton } from '../image-button';
import { RecordAudioButton } from '../record-audio-button';
import { RecordVideoButton } from '../record-video-button';

export type DecorationButtonVariant = 'default' | 'list-item';

const decorations: { module: ActionModule; component: ReactElement }[] = [
	{
		module: 'emoji',
		component: <EmojiButton />,
	},
	{
		module: 'gif',
		component: <GifButton />,
	},
	{
		module: 'image',
		component: <ImageButton />,
	},
	{
		module: 'attachment',
		component: <AttachmentButton />,
	},
	{
		module: 'mention',
		component: <MentionButton />,
	},
	{
		module: 'video-recording',
		component: <RecordVideoButton />,
	},
	{
		module: 'audio-recording',
		component: <RecordAudioButton />,
	},
];

export const useDecorationButtons = () => {
	const { modules } = useActionInput();
	// I'd rather do this with <Show/Hide> or a style value, but there are issues due to the fact
	// that these buttons display in a button group AND require a tooltip, which interferes with the
	// layout since we need to wrap them in a <Box> for ref support
	const showEmoji = useBreakpointValue({ base: false, xs: true });
	const showMobileCollapsedView = useBreakpointValue({ base: true, sm: false });
	const decorationButtonVariant = showMobileCollapsedView ? 'list-item' : 'default';
	const showAudioRecording = useFeatureFlag('audio-attachment');

	return useMemo(() => {
		return decorations
			.filter((dec) => {
				if (dec.module === 'emoji' && !showEmoji) {
					return false;
				}

				if (dec.module === 'audio-recording' && !showAudioRecording) {
					return false;
				}

				return modules.includes(dec.module);
			})
			.map((dec) => {
				return cloneElement(dec.component, { key: dec.module, decorationVariant: decorationButtonVariant });
			});
	}, [modules, showEmoji, showAudioRecording, decorationButtonVariant]);
};
